import { useEffect, useRef, useState } from 'react';
import {
  createBoundaryValidator,
  OverlayFrameSchema,
  OVERLAY_STRUCTURAL_EVENT_SCHEMAS,
} from '@sleeved/lib';
import type { BoundaryId, ValidationMode } from '@sleeved/lib';
import type { RoomMeta, AnimationSettings } from '../types/widgets';
import { reportSchemaValidationMismatch } from '../utils/schemaValidationReporter';

interface UseWsEventsReturn<T> {
  data: T;
  roomMeta: RoomMeta | null;
  animationSettings: AnimationSettings | null;
  connected: boolean;
}

// ---------------------------------------------------------------------------
// D123a — overlay WS ingress boundary (monitor mode).
//
// The single monitor/enforce switch point for the overlay boundary is this one
// module-level const. A future enforce flip changes only this line.
// ---------------------------------------------------------------------------
const BOUNDARY_MODE: ValidationMode = 'monitor';
const WS_OVERLAY_BOUNDARY: BoundaryId = 'ws-overlay';
const validate = createBoundaryValidator(reportSchemaValidationMismatch);

interface OverlayWsFrame {
  type?: string;
  events?: Array<{ eventName?: string; data?: Record<string, unknown> }>;
  eventName?: string;
  data?: Record<string, unknown>;
}

/**
 * Pure validate-and-forward for one inbound overlay WS frame (monitor mode).
 * Extracted from the hook so it is directly callable under vitest's node
 * environment (no jsdom, no WebSocket mock).
 *
 * Two-family validation (spec §2):
 *   Family 1 — `OverlayFrameSchema` on the whole frame (FULL_STATE batch vs
 *              single `{eventName, data}`).
 *   Family 2 — per-event tier split: STRUCTURAL events (config + payloads) get
 *              their `OVERLAY_STRUCTURAL_EVENT_SCHEMAS` schema; HOT events
 *              (`card`, `videoTransforms`) and any unknown eventName are
 *              envelope-only and NEVER deep-validated. This mirrors the web
 *              ROOM_DELTA per-field split for cross-boundary consistency.
 *
 * ALWAYS returns the ORIGINAL `frame` reference unchanged — validation is
 * signal-only, the parsed clone is never forwarded.
 */
export function validateOverlayFrame<F>(frame: F): F {
  validate(OverlayFrameSchema, frame, {
    boundary: WS_OVERLAY_BOUNDARY,
    schemaName: 'OverlayFrame',
    mode: BOUNDARY_MODE,
  });

  const f = frame as OverlayWsFrame;
  const events =
    f?.type === 'FULL_STATE' && Array.isArray(f.events)
      ? f.events
      : f && typeof f.eventName === 'string'
        ? [{ eventName: f.eventName, data: f.data }]
        : [];

  for (const evt of events) {
    if (!evt || typeof evt.eventName !== 'string') continue;
    const structuralSchema = OVERLAY_STRUCTURAL_EVENT_SCHEMAS[evt.eventName];
    if (structuralSchema) {
      validate(structuralSchema, evt.data, {
        boundary: WS_OVERLAY_BOUNDARY,
        schemaName: `overlayEvent.${evt.eventName}`,
        mode: BOUNDARY_MODE,
      });
    }
  }

  return frame;
}

/**
 * Hook that connects to the API WebSocket and listens for overlay events.
 *
 * Authenticates with `{ type: 'OVERLAY', token }` on open. Handles both
 * single `{ eventName, data }` frames and the initial `{ type: 'FULL_STATE', events: [...] }`
 * batch delivered on connect.
 *
 * @param eventNamesCsv - Comma-separated event names to listen for (stable string, not array)
 * @param initialValue - Initial data state
 * @param explicitToken - Optional token override; if provided, used instead of reading from URL params
 */
export function useWsEvents<T>(
  eventNamesCsv: string,
  initialValue: T,
  explicitToken?: string,
): UseWsEventsReturn<T> {
  const [data, setData] = useState<T>(initialValue);
  const [roomMeta, setRoomMeta] = useState<RoomMeta | null>(null);
  const [animationSettings, setAnimationSettings] = useState<AnimationSettings | null>(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    const token = explicitToken ?? new URLSearchParams(window.location.search).get('token');
    if (!token) {
      console.warn('[useWsEvents] No token found in URL query params');
      return;
    }

    const wsUrl = import.meta.env.VITE_WEBSOCKET_URL;
    console.log(`[useWsEvents] Connecting to WS | url=${wsUrl} | events=${eventNamesCsv}`);
    let disposed = false;
    let reconnectTimerId: ReturnType<typeof setTimeout> | undefined;
    const eventNames = new Set(eventNamesCsv ? eventNamesCsv.split(',') : []);

    // Sidecar events -- always routed regardless of eventNamesCsv
    const sidecarEvents = new Set(['roomMeta', 'animationSettings']);

    /** Route a single { eventName, data } event to the correct state setter. */
    const routeEvent = (eventName: string, payload: Record<string, unknown>) => {
      // Handle sidecar events
      if (sidecarEvents.has(eventName) && !eventNames.has(eventName)) {
        if (eventName === 'roomMeta') setRoomMeta(payload as unknown as RoomMeta);
        if (eventName === 'animationSettings') setAnimationSettings(payload as unknown as AnimationSettings);
        return;
      }

      // Handle requested events
      if (eventNames.has(eventName)) {
        setData((prev) => {
          const keys = Object.keys(payload);
          if (keys.length > 0 && keys.every(k => (prev as Record<string, unknown>)[k] === payload[k])) return prev;
          return { ...prev, ...payload };
        });
      }
    };

    const connect = () => {
      // Close existing connection before creating new one
      wsRef.current?.close();

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (disposed) return;
        console.log(`[useWsEvents] WS connection opened, authenticating`);
        ws.send(JSON.stringify({ type: 'OVERLAY', token }));
        // Do not set connected=true here — wait for FULL_STATE which confirms the server
        // accepted the token. A 4403 close after onopen would cause a brief flicker otherwise.
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        if (disposed) return;

        let parsed: Record<string, unknown>;
        try {
          parsed = JSON.parse(event.data as string);
        } catch {
          return;
        }

        // D123a — monitor-mode ingress validation (signal only; the ORIGINAL
        // parsed frame is forwarded to routing regardless of the outcome).
        validateOverlayFrame(parsed);

        // Handle FULL_STATE batch delivered on connect — this is the de facto auth confirmation
        if (parsed.type === 'FULL_STATE' && Array.isArray(parsed.events)) {
          setConnected(true);
          for (const evt of parsed.events as Array<{ eventName: string; data: Record<string, unknown> }>) {
            if (evt.eventName && evt.data) {
              routeEvent(evt.eventName, evt.data);
            }
          }
          return;
        }

        // Handle single event frame
        const eventName = parsed.eventName as string | undefined;
        if (eventName && parsed.data && typeof parsed.data === 'object') {
          routeEvent(eventName, parsed.data as Record<string, unknown>);
        }
      };

      ws.onclose = () => {
        if (disposed) return;
        setConnected(false);
        wsRef.current = null;
        const cappedAttempts = Math.min(reconnectAttempts.current, 5);
        const delay = Math.min(1000 * Math.pow(2, cappedAttempts), 30000) + Math.random() * 1000;
        reconnectAttempts.current++;
        console.log(`[useWsEvents] Connection closed, reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttempts.current})`);
        reconnectTimerId = setTimeout(connect, delay);
      };

      ws.onerror = (err) => {
        if (disposed) return;
        console.error('[useWsEvents] WS error:', err);
        // onclose will fire after onerror, triggering reconnect
      };
    };

    connect();

    return () => {
      disposed = true;
      clearTimeout(reconnectTimerId);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [eventNamesCsv, explicitToken]); // stable string dependencies

  return { data, roomMeta, animationSettings, connected };
}
